import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Command, Layers, Shield } from 'lucide-react';
import { ENTERPRISE_ADMIN_BASE } from '../../config/adminLayers';

const E = ENTERPRISE_ADMIN_BASE;

const cards = [
  {
    title: 'Directory & tenants',
    description: 'Users and workspaces in this shell; same page components as classic.',
    links: [
      { label: 'Users', to: `${E}/users` },
      { label: 'Workspace directory (360°)', to: `${E}/workspaces` },
      { label: 'Workspaces (full table)', to: `${E}/workspaces/table` },
    ],
  },
  {
    title: 'Email & domains',
    description: 'Cross-tenant verified sending domains with drill-down to workspace 360°.',
    links: [{ label: 'Sending domains', to: `${E}/verified-domains` }],
  },
  {
    title: 'Revenue',
    description: 'Invoices and pricing — mirrored routes, unchanged APIs.',
    links: [
      { label: 'Invoices', to: `${E}/invoices` },
      { label: 'Pricing', to: `${E}/pricing` },
    ],
  },
  {
    title: 'Platform credentials',
    description: 'API keys, telephony, workflows, integrations.',
    links: [
      { label: 'API keys', to: `${E}/api-keys` },
      { label: 'Phone numbers', to: `${E}/phone-numbers` },
      { label: 'Calling providers', to: `${E}/calling-providers` },
      { label: 'Workflows', to: `${E}/workflows` },
      { label: 'Workflow health', to: `${E}/workflow-health` },
    ],
  },
  {
    title: 'Reliability & compliance',
    description: 'Health monitors; audit log UI is staged pending backend.',
    links: [
      { label: 'System', to: `${E}/system` },
      { label: 'Monitoring', to: `${E}/monitoring` },
      { label: 'Audit log (roadmap)', to: `${E}/audit` },
    ],
  },
];

/**
 * Enterprise home: overview + links for enterprise routes (mirrored + enterprise-only).
 */
export const EnterpriseHomePage: React.FC = () => {
  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="rounded-2xl border border-enterprise-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-600">Enterprise console</p>
            <h2 className="mt-2 text-2xl font-bold text-enterprise-900 sm:text-3xl">Command overview</h2>
            <p className="mt-3 max-w-2xl text-sm text-enterprise-600 sm:text-base">
              Most admin pages are available here under <code className="rounded bg-enterprise-50 px-1">{E}</code> — same
              components and APIs as classic, with this shell, sidebar, and{' '}
              <kbd className="rounded bg-enterprise-100 px-1.5 py-0.5 text-enterprise-800">⌘K</kbd> jump menu.
              Classic URLs under <code className="rounded bg-enterprise-50 px-1">/admin/…</code> are unchanged.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:items-end">
            <Link
              to={`${E}/dashboard`}
              className="inline-flex items-center gap-2 rounded-lg border border-enterprise-300 bg-enterprise-50 px-4 py-2 text-sm font-medium text-enterprise-800 hover:bg-enterprise-100"
            >
              <Layers className="h-4 w-4" />
              Ops dashboard
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/admin/dashboard"
              className="inline-flex items-center gap-2 rounded-lg border border-dashed border-enterprise-300 px-4 py-2 text-xs font-medium text-enterprise-600 hover:bg-enterprise-50"
            >
              Classic layout (legacy sidebar)
            </Link>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 text-xs text-enterprise-600">
          <span className="inline-flex items-center gap-1 rounded-full bg-enterprise-100 px-3 py-1 font-medium text-enterprise-800">
            <Shield className="h-3.5 w-3.5" />
            No breaking changes to legacy routes
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-enterprise-100 px-3 py-1 font-medium text-enterprise-800">
            <Command className="h-3.5 w-3.5" />
            Cmd+K / Ctrl+K
          </span>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <div
            key={card.title}
            className="rounded-xl border border-enterprise-200 bg-white p-5 shadow-sm transition hover:border-emerald-200 hover:shadow-md"
          >
            <h3 className="text-lg font-semibold text-enterprise-900">{card.title}</h3>
            <p className="mt-1 text-sm text-enterprise-600">{card.description}</p>
            <ul className="mt-4 space-y-2">
              {card.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="group inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900"
                  >
                    {l.label}
                    <ArrowRight className="h-4 w-4 opacity-70 transition group-hover:translate-x-0.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-dashed border-enterprise-300 bg-enterprise-50/50 p-5">
        <div className="flex items-start gap-3">
          <BookOpen className="h-5 w-5 shrink-0 text-enterprise-500" />
          <div>
            <p className="text-sm font-semibold text-enterprise-900">Implementation plan</p>
            <p className="mt-1 text-sm text-enterprise-600">
              See <code className="rounded bg-white px-1.5">documentation/ADMIN_APP_ENTERPRISE_IMPLEMENTATION_PLAN.md</code>{' '}
              and <code className="rounded bg-white px-1.5">ADMIN_PANEL_ENTERPRISE_FEATURE_LIST.md</code> for roadmap,
              §0 frontend coverage, and phased rollout.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
